import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { httpsCallable } from 'firebase/functions';
import { useAuth } from '@/hooks/useAuth';
import { ArrowRight, Zap, Target, Globe, Mail, CheckCircle, Star, Phone, Terminal, Loader2, Send, PackageCheck, TrendingUp, Sparkles, Wrench, Wind, Trees, Home as HomeIcon, Moon, Sun, Briefcase, Rocket } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer } from 'recharts';
import { functions, logAnalyticsEvent } from '@/lib/firebase';
import { Button } from '@/components/ui/Button';
import { CheckoutTransition } from '@/components/ui/CheckoutTransition';
import { HeroBackground } from '@/components/three';
import { prefetchCheckoutSubscription } from '@/lib/checkoutPrefetch';
import { getHostedCheckoutUrl } from '@/lib/hostedCheckoutPrefetch';
import { getMetaBrowserIds, metaInitiateCheckout } from '@/lib/metaPixel';
import { PLAN_CONFIGS, type PlanId } from '@shared/types';
import { OnboardingFunnel } from './OnboardingFunnel';
import { useIsMobile, isMobileNow, isPhoneDevice } from '@/hooks/useIsMobile';

// Mobile haptic feedback helper - silent no-op on unsupported platforms.
const haptic = (pattern: number | number[] = 12) => {
  if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
    try { navigator.vibrate(pattern); } catch { /* noop */ }
  }
};

// Type-on effect for headlines. Returns { text, done } so the caller can
// hide the cursor when typing finishes.
function useTypewriter(text: string, speed = 130, startDelay = 200) {
  const [displayed, setDisplayed] = useState('');
  const [done, setDone] = useState(false);

  useEffect(() => {
    setDisplayed('');
    setDone(false);
    let i = 0;
    let interval: ReturnType<typeof setInterval> | undefined;
    const start = setTimeout(() => {
      interval = setInterval(() => {
        i++;
        setDisplayed(text.slice(0, i));
        if (i >= text.length) {
          if (interval) clearInterval(interval);
          setDone(true);
        }
      }, speed);
    }, startDelay);
    return () => {
      clearTimeout(start);
      if (interval) clearInterval(interval);
    };
  }, [text, speed, startDelay]);

  return { text: displayed, done };
}

// Smooth ease-out count-up for big numbers.
function useCountUp(target: number, duration = 1600, trigger: unknown = target) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (target <= 0) { setValue(0); return; }
    let raf = 0;
    const start = performance.now();
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      setValue(Math.round(target * eased));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trigger, target, duration]);
  return value;
}

// ============================================================
// Constants reused by desktop Income Calculator sections
// ============================================================

type HoursId = '2-3' | '4-6' | '7-10' | '10+';
type PriceTier = 300 | 500 | 800 | 1200;

const HOURS_OPTIONS: { id: HoursId; label: string; sub: string; closes: number; Icon: typeof Moon }[] = [
  { id: '2-3',  label: '2–3 hrs / week',  sub: 'Evenings only',          closes: 3,  Icon: Moon },
  { id: '4-6',  label: '4–6 hrs / week',  sub: 'Real money on weekends', closes: 7,  Icon: Sun },
  { id: '7-10', label: '7–10 hrs / week', sub: 'Quit-the-day-job pace',  closes: 14, Icon: Briefcase },
  { id: '10+',  label: '10+ hrs / week',  sub: 'Go full send',           closes: 22, Icon: Rocket },
];

const PRICE_OPTIONS: { id: PriceTier; label: string; sub: string }[] = [
  { id: 300,  label: '$300',   sub: 'Easiest yes' },
  { id: 500,  label: '$500',   sub: 'Sweet spot' },
  { id: 800,  label: '$800',   sub: 'Premium positioning' },
  { id: 1200, label: '$1,200', sub: 'Agency tier' },
];

const NICHE_OPTIONS: { id: string; label: string; Icon: typeof Wrench }[] = [
  { id: 'plumbers',     label: 'Plumbers',     Icon: Wrench },
  { id: 'electricians', label: 'Electricians', Icon: Zap },
  { id: 'hvac',         label: 'HVAC Techs',   Icon: Wind },
  { id: 'landscapers',  label: 'Landscapers',  Icon: Trees },
  { id: 'roofers',      label: 'Roofers',      Icon: HomeIcon },
  { id: 'cleaners',     label: 'Cleaners',     Icon: Sparkles },
];

// ============================================================
// Demo leads data - fictional businesses for demonstration
// ============================================================
const demoLeads = [
  { name: "Riverstone Electric Co", rating: 4.8, reviews: 127, location: 'Riverside, CA', hasEmail: true, hasPhone: true },
  { name: 'Clearwater Power Services', rating: 4.6, reviews: 89, location: 'Lakewood, CA', hasEmail: false, hasPhone: true },
  { name: 'Suncoast Wiring Solutions', rating: 4.9, reviews: 203, location: 'Sunnyvale, CA', hasEmail: true, hasPhone: true },
  { name: 'Palm Bay Electric Pros', rating: 4.7, reviews: 156, location: 'Palm Springs, CA', hasEmail: true, hasPhone: false },
  { name: 'Coastal Current Electric', rating: 4.5, reviews: 74, location: 'Oceanside, CA', hasEmail: false, hasPhone: true },
];

const typingParagraph = "Family-owned since 1985. Fast, reliable electrical services for your home and business. Available 24/7.";

type LogEntry = {
  text: string;
  type: 'info' | 'success' | 'system' | 'lead';
};

function LiveDemoWindow() {
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [visibleLeads, setVisibleLeads] = useState<number[]>([]);
  const [showSite, setShowSite] = useState(false);
  const [typedChars, setTypedChars] = useState(0);
  const [showNav, setShowNav] = useState(false);
  const [showHero, setShowHero] = useState(false);
  const [showImage, setShowImage] = useState(false);
  const [showButtons, setShowButtons] = useState(false);
  const [price, setPrice] = useState<number | null>(null);
  const [priceTypedChars, setPriceTypedChars] = useState(0);
  const [showSale, setShowSale] = useState(false);
  const [saleMessage, setSaleMessage] = useState<'sending' | 'waiting' | 'closed' | null>(null);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [phase, setPhase] = useState(0);
  const logsRef = useRef<HTMLDivElement>(null);
  const demoRef = useRef<HTMLDivElement>(null);

  const getTime = () => new Date().toLocaleTimeString('en-US', { hour12: false });

  useEffect(() => {
    if (logsRef.current) {
      logsRef.current.scrollTop = logsRef.current.scrollHeight;
    }
  }, [logs]);

  useEffect(() => {
    const timers: NodeJS.Timeout[] = [];

    const addLog = (entry: LogEntry, delay: number) => {
      const timer = setTimeout(() => {
        setLogs(prev => [...prev, entry]);
      }, delay);
      timers.push(timer);
      return delay;
    };

    const addLead = (index: number, delay: number) => {
      const timer = setTimeout(() => {
        setVisibleLeads(prev => [...prev, index]);
        setLogs(prev => [...prev, {
          text: `Found: ${demoLeads[index].name}`,
          type: 'lead',
        }]);
      }, delay);
      timers.push(timer);
      return delay;
    };

    setLogs([]);
    setVisibleLeads([]);
    setShowSite(false);
    setTypedChars(0);
    setShowNav(false);
    setShowHero(false);
    setShowImage(false);
    setShowButtons(false);
    setPrice(null);
    setPriceTypedChars(0);
    setShowSale(false);
    setSaleMessage(null);
    setAiGenerating(false);

    let t = 0;

    t = addLog({ text: 'Connecting to Google Places API...', type: 'info' }, t + 2000);
    t = addLog({ text: '✓ Connected', type: 'success' }, t + 800);
    t = addLog({ text: 'Scanning California electricians...', type: 'system' }, t + 400);

    t = addLead(0, t + 350);
    t = addLead(1, t + 250);
    t = addLead(2, t + 250);
    t = addLead(3, t + 250);
    t = addLead(4, t + 250);

    t = addLog({ text: '✓ Found 47 without websites', type: 'success' }, t + 500);
    t = addLog({ text: 'Generating site for Riverstone...', type: 'info' }, t + 600);

    timers.push(setTimeout(() => {
      setShowSite(true);
      setAiGenerating(true);
    }, t + 400));
    t += 400;

    timers.push(setTimeout(() => setShowNav(true), t + 300));
    t += 300;
    timers.push(setTimeout(() => setShowHero(true), t + 350));
    t += 350;
    timers.push(setTimeout(() => setShowImage(true), t + 400));
    t += 400;

    const startTypingAt = t;
    for (let i = 1; i <= typingParagraph.length; i++) {
      timers.push(setTimeout(() => setTypedChars(i), startTypingAt + i * 12));
    }
    t += typingParagraph.length * 12 + 200;

    timers.push(setTimeout(() => setShowButtons(true), t));
    t += 300;

    timers.push(setTimeout(() => setAiGenerating(false), t));

    t = addLog({ text: '✓ Site generated!', type: 'success' }, t + 150);

    const priceStr = '299';
    timers.push(setTimeout(() => setPrice(0), t + 300));
    t += 300;
    for (let i = 1; i <= priceStr.length; i++) {
      timers.push(setTimeout(() => setPriceTypedChars(i), t + i * 150));
    }
    t += priceStr.length * 150 + 100;
    timers.push(setTimeout(() => setPrice(299), t));
    t += 100;

    t = addLog({ text: '✓ Price set: $299', type: 'success' }, t + 200);

    t = addLog({ text: 'Sending to lead...', type: 'info' }, t + 300);
    timers.push(setTimeout(() => setSaleMessage('sending'), t + 100));
    t += 400;
    timers.push(setTimeout(() => setSaleMessage('waiting'), t));
    t = addLog({ text: '✓ Site sent!', type: 'success' }, t + 100);
    t += 800;

    timers.push(setTimeout(() => {
      setSaleMessage('closed');
      setLogs(prev => [...prev, { text: '🎉 SALE CLOSED!', type: 'success' }]);
      setShowSale(true);
    }, t));
    t += 100;

    timers.push(setTimeout(() => {
      setPhase(p => p + 1);
    }, t + 3500));

    return () => timers.forEach(t => clearTimeout(t));
  }, [phase]);

  const getLogIcon = (type: LogEntry['type']) => {
    switch (type) {
      case 'success': return <CheckCircle className="w-3 h-3 text-primary-400 flex-shrink-0" />;
      case 'system': return <Terminal className="w-3 h-3 text-primary-300 flex-shrink-0" />;
      case 'lead': return <Globe className="w-3 h-3 text-primary-400 flex-shrink-0" />;
      default: return <div className="w-3 h-3 rounded-full bg-primary-400/50 animate-pulse flex-shrink-0" />;
    }
  };

  const getLogColor = (type: LogEntry['type']) => {
    switch (type) {
      case 'success': return 'text-primary-400';
      case 'system': return 'text-primary-300';
      case 'lead': return 'text-primary-400';
      default: return 'text-gray-400';
    }
  };

  return (
    <div className="relative w-full">
      <div
        ref={demoRef}
        className="relative rounded-2xl overflow-hidden border border-primary-500/30"
        style={{
          background: 'linear-gradient(180deg, rgba(0,0,0,0.95) 0%, rgba(0,0,0,0.98) 100%)',
          boxShadow: '0 50px 100px -20px rgba(0,0,0,0.9), 0 0 80px rgba(91,58,58,0.15)',
        }}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-primary-500/20 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="relative">
              <div
                className="w-16 h-16 rounded-xl border-2 border-white bg-black flex items-center justify-center overflow-hidden"
                style={{ animation: 'pulse 2s ease-in-out infinite', boxShadow: '0 0 20px rgba(255,255,255,0.2)' }}
              >
                <img src="/wolfromulus.png" alt="Romulus logo" className="w-14 h-14 object-contain" />
              </div>
              <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-white rounded-full animate-ping" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white tracking-wider">LIVE DEMO</h2>
              <p className="text-white/60 text-xs">Scrape → Generate → Sell</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Loader2 className="w-4 h-4 text-primary-400 animate-spin" />
            <span className="text-primary-400 text-xs font-mono">LIVE</span>
            <span className="w-2 h-2 rounded-full bg-primary-400 animate-pulse" />
          </div>
        </div>

        {/* Stats bar */}
        <div className="px-6 py-2 bg-primary-500/5 border-b border-primary-500/20 flex gap-6 flex-wrap font-mono text-xs">
          <div>
            <span className="text-primary-500/60">QUERY:</span>
            <span className="text-primary-400 ml-2">Electrician</span>
          </div>
          <div>
            <span className="text-primary-500/60">LOCATION:</span>
            <span className="text-primary-400 ml-2">Miami, FL</span>
          </div>
          <div>
            <span className="text-primary-500/60">FOUND:</span>
            <span className="text-primary-400 ml-2">{visibleLeads.length}</span>
          </div>
        </div>

        {/* Content - Three columns */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-0" style={{ height: '520px' }}>
          {/* Terminal */}
          <div className="border-r border-primary-500/20 flex flex-col">
            <div className="px-3 py-2 bg-primary-500/10 border-b border-primary-500/20 flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-red-500/80" />
              <div className="w-2 h-2 rounded-full bg-yellow-500/80" />
              <div className="w-2 h-2 rounded-full bg-primary-500/80" />
              <span className="ml-2 text-primary-500/60 text-xs font-mono">romulus executing tasks...</span>
            </div>
            <div
              ref={logsRef}
              className="flex-1 overflow-auto p-4 font-mono text-xs"
              style={{ background: 'rgba(0,0,0,0.5)' }}
            >
              {logs.map((log, i) => (
                <div key={i} className="mb-3 demo-fade-in">
                  <div className="flex items-start gap-2">
                    <span className="text-primary-500/40 text-xs">[{getTime()}]</span>
                    {getLogIcon(log.type)}
                    <span className={`${getLogColor(log.type)} ${log.text.includes('SALE') ? 'font-bold' : ''}`}>
                      {log.text}
                    </span>
                  </div>
                </div>
              ))}
              {!showSale && (
                <div className="flex items-center gap-2 mt-2">
                  <span className="text-primary-500/40 text-xs">[{getTime()}]</span>
                  <Loader2 className="w-3 h-3 text-primary-400 animate-spin" />
                  <span className="text-primary-400 animate-pulse">▊</span>
                </div>
              )}
            </div>
          </div>

          {/* Leads Found */}
          <div className="border-r border-primary-500/20 flex flex-col">
            <div className="px-3 py-2 bg-primary-500/10 border-b border-primary-500/20 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Target className="w-3 h-3 text-yellow-400" />
                <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Leads</span>
              </div>
              {visibleLeads.length > 0 && (
                <span className="text-xs text-primary-400 font-medium">{visibleLeads.length} found</span>
              )}
            </div>
            <div className="flex-1 overflow-auto p-2 space-y-2" style={{ background: 'rgba(0,0,0,0.3)' }}>
              {visibleLeads.map((leadIdx, i) => {
                const lead = demoLeads[leadIdx];
                const isFirst = i === 0;
                return (
                  <div
                    key={i}
                    className={`p-2.5 rounded-lg border demo-fade-in ${
                      isFirst && showSite
                        ? 'bg-primary-500/10 border-primary-500/30'
                        : 'bg-white/5 border-white/10'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <div className={`w-7 h-7 rounded flex items-center justify-center flex-shrink-0 ${
                        isFirst && showSite ? 'bg-primary-500/20' : 'bg-yellow-500/20'
                      }`}>
                        {isFirst && showSite ? (
                          <CheckCircle className="w-3.5 h-3.5 text-primary-400" />
                        ) : (
                          <Globe className="w-3.5 h-3.5 text-yellow-400" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-white font-medium truncate">{lead.name}</p>
                        <div className="flex items-center gap-2 text-xs text-gray-500">
                          <span className="flex items-center gap-0.5">
                            <Star className="w-2.5 h-2.5 text-yellow-400 fill-current" />
                            {lead.rating}
                          </span>
                          <span className="truncate">{lead.location}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 mt-2 ml-9">
                      {lead.hasEmail && (
                        <span className="flex items-center gap-1 text-xs text-primary-400">
                          <Mail className="w-2.5 h-2.5" />
                          Email
                        </span>
                      )}
                      {lead.hasPhone && (
                        <span className="flex items-center gap-1 text-xs text-yellow-400">
                          <Phone className="w-2.5 h-2.5" />
                          Phone
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Generated Site Preview */}
          <div className="flex flex-col relative">
            <div className="px-3 py-2 bg-primary-500/10 border-b border-primary-500/20 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Globe className="w-3 h-3 text-blue-400" />
                <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Preview</span>
              </div>
              {aiGenerating && (
                <div className="flex items-center gap-2">
                  <Loader2 className="w-3 h-3 text-primary-400 animate-spin" />
                  <span className="text-xs text-primary-400 font-medium">AI generating...</span>
                </div>
              )}
            </div>

            <div className="flex-1 overflow-hidden relative" style={{ background: '#000000' }}>
              {showSite ? (
                <div className="absolute inset-0 flex flex-col" style={{ background: '#ffffff' }}>
                  <div
                    className="px-3 py-2 flex items-center justify-between border-b transition-opacity duration-500"
                    style={{ opacity: showNav ? 1 : 0, borderColor: '#e5e7eb' }}
                  >
                    <span className="font-bold text-sm" style={{ color: '#1f2937' }}>Riverstone Electric</span>
                    <div className="flex gap-2 text-xs" style={{ color: '#6b7280' }}>
                      <span>About</span>
                      <span>Services</span>
                      <span>Contact</span>
                    </div>
                  </div>

                  <div className="flex-1 flex" style={{ background: '#f9fafb' }}>
                    <div
                      className="w-1/2 p-3 flex flex-col justify-center transition-opacity duration-700"
                      style={{ opacity: showHero ? 1 : 0 }}
                    >
                      <h1 className="font-bold text-sm mb-1 leading-tight" style={{ color: '#1f2937' }}>
                        Riverstone Electric Co
                      </h1>
                      <p className="text-xs mb-2" style={{ color: '#3b82f6' }}>
                        Professional Electrical Services in Riverside, CA
                      </p>
                      <p className="text-xs leading-relaxed mb-3 h-[40px] overflow-hidden" style={{ color: '#4b5563' }}>
                        {typingParagraph.slice(0, typedChars)}
                        {typedChars < typingParagraph.length && typedChars > 0 && (
                          <span className="text-primary-500 animate-pulse">|</span>
                        )}
                      </p>
                      <div
                        className="flex items-center gap-2 transition-opacity duration-500 flex-shrink-0"
                        style={{ opacity: showButtons ? 1 : 0 }}
                      >
                        <div className="px-2.5 py-1 bg-blue-600 rounded text-xs text-white font-semibold whitespace-nowrap">
                          Call Now
                        </div>
                        <div className="px-2.5 py-1 border border-gray-300 rounded text-xs whitespace-nowrap" style={{ color: '#374151' }}>
                          Free Quote
                        </div>
                      </div>
                    </div>

                    <div
                      className="w-1/2 p-2 transition-opacity duration-700"
                      style={{ opacity: showImage ? 1 : 0 }}
                    >
                      <div
                        className="h-full rounded-lg overflow-hidden shadow-lg"
                        style={{
                          backgroundImage: 'url(https://images.unsplash.com/photo-1621905251918-48416bd8575a?w=600&q=80)',
                          backgroundSize: 'cover',
                          backgroundPosition: 'center',
                        }}
                      />
                    </div>
                  </div>

                  <div
                    className="px-4 py-3 border-t flex items-center justify-between transition-opacity duration-500"
                    style={{ opacity: price !== null ? 1 : 0, borderColor: '#e5e7eb', background: '#ffffff' }}
                  >
                    <div>
                      <p className="text-xs" style={{ color: '#6b7280' }}>Your Price</p>
                      <div className="flex items-center gap-1">
                        <span className="text-lg" style={{ color: '#374151' }}>$</span>
                        <span className="text-xl font-bold" style={{ color: '#1f2937' }}>
                          {price === 0 ? '299'.slice(0, priceTypedChars) : price}
                          {price === 0 && priceTypedChars < 3 && (
                            <span className="text-primary-500 animate-pulse">|</span>
                          )}
                        </span>
                      </div>
                    </div>
                    {saleMessage === 'sending' && (
                      <div className="flex items-center gap-2">
                        <Loader2 className="w-3 h-3 text-blue-500 animate-spin" />
                        <span className="text-xs text-blue-500 font-medium">Sending...</span>
                      </div>
                    )}
                    {saleMessage === 'waiting' && (
                      <div className="flex items-center gap-2">
                        <Loader2 className="w-3 h-3 text-yellow-500 animate-spin" />
                        <span className="text-xs text-yellow-600 font-medium">Waiting for response...</span>
                      </div>
                    )}
                    {!saleMessage && price !== null && price > 0 && (
                      <p className="text-xs text-green-600 font-medium">You keep 100%</p>
                    )}
                  </div>
                </div>
              ) : (
                <div className="absolute inset-0 flex items-center justify-center text-center p-4" style={{ background: '#000000' }}>
                  <div>
                    <Globe className="w-12 h-12 mx-auto mb-3 text-gray-600 opacity-40" />
                    <p className="text-sm text-gray-500">Waiting for generation...</p>
                  </div>
                </div>
              )}

              {showSale && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/85 backdrop-blur-sm z-20 demo-fade-in">
                  <div
                    className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-8 text-center shadow-2xl demo-bounce-in"
                    style={{ boxShadow: '0 0 100px rgba(34,197,94,0.6)' }}
                  >
                    <CheckCircle className="w-14 h-14 text-white mx-auto mb-4" />
                    <p className="text-white/80 text-sm font-medium mb-2">SALE CLOSED!</p>
                    <p className="text-white text-4xl font-bold">$299</p>
                    <p className="text-white/80 text-sm mt-3 font-semibold">
                      You earned $299 (100%)
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

      </div>

      <style>{`
        .demo-fade-in {
          animation: demoFadeIn 0.5s ease-out;
        }
        .demo-bounce-in {
          animation: demoBounceIn 0.6s ease-out;
        }
        @keyframes demoFadeIn {
          from { opacity: 0; transform: translateY(5px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes demoBounceIn {
          0% { opacity: 0; transform: scale(0.8); }
          50% { transform: scale(1.05); }
          100% { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </div>
  );
}

// ============================================================
// Mobile: Compact live-activity ticker
// ============================================================

function MobileLiveTicker() {
  const [step, setStep] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setStep((s) => (s + 1) % 8);
    }, 600);
    return () => clearInterval(timer);
  }, []);

  const lines: { icon: string; text: string; color: string }[] = [
    { icon: '▶', text: 'scanning plumbers in Austin', color: 'text-gray-400' },
    { icon: '✓', text: '47 without websites', color: 'text-green-400' },
    { icon: '✓', text: '47 sites built', color: 'text-green-400' },
    { icon: '◆', text: '47 SMS pitches sent', color: 'text-blue-400' },
    { icon: '$', text: '1 sold · auto-delivering files', color: 'text-green-400' },
    { icon: '✓', text: '$300 in your Stripe', color: 'text-green-400' },
  ];

  return (
    <div
      className="w-full max-w-sm rounded-xl border border-white/10 bg-black/60 backdrop-blur-sm overflow-hidden mb-6"
      style={{ boxShadow: '0 0 60px rgba(91,58,58,0.2)' }}
    >
      <div className="flex items-center gap-1.5 px-3 py-2 border-b border-white/10 bg-white/[0.03]">
        <div className="w-2 h-2 rounded-full bg-red-500/60" />
        <div className="w-2 h-2 rounded-full bg-yellow-500/60" />
        <div className="w-2 h-2 rounded-full bg-green-500/60" />
        <span className="text-[10px] text-gray-500 ml-2 font-mono tracking-wide">romulus.live</span>
        <span className="ml-auto text-[10px] text-green-400 font-mono flex items-center gap-1">
          <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
          LIVE
        </span>
      </div>
      <div className="p-4 font-mono text-[11px] space-y-1.5 min-h-[150px]">
        {lines.map((line, i) => (
          <div
            key={i}
            className={`flex items-start gap-2 transition-all duration-200 ${
              i < step ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-2'
            }`}
          >
            <span className={`${line.color} flex-shrink-0 w-3`}>{line.icon}</span>
            <span className="text-gray-300 leading-relaxed">{line.text}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================================
// Desktop: CLI window frame - transparent glass shell
// ============================================================

function CLIFrame({ title, subtitle, children }: { title: string; subtitle?: string; children: React.ReactNode }) {
  return (
    <div
      className="rounded-2xl overflow-hidden border border-white/[0.12] backdrop-blur-sm"
      style={{ background: 'rgba(0,0,0,0.35)' }}
    >
      {/* Window chrome */}
      <div className="flex items-center gap-3 px-5 py-3 border-b border-white/[0.08]" style={{ background: 'rgba(255,255,255,0.03)' }}>
        <div className="flex items-center gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full bg-red-500/60" />
          <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/60" />
          <div className="w-2.5 h-2.5 rounded-full bg-green-500/60" />
        </div>
        <span className="text-xs text-gray-500 font-mono tracking-wide">{title}</span>
        {subtitle && (
          <span className="ml-auto text-[10px] text-primary-400 font-mono flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-primary-400 animate-pulse" />
            {subtitle}
          </span>
        )}
      </div>
      {/* Content */}
      <div className="p-8">
        {children}
      </div>
    </div>
  );
}

// ============================================================
// Desktop: Income Calculator section
// ============================================================

function IncomeCalculator({
  selectedHours,
  selectedPrice,
  onSelectHours,
  onSelectPrice,
}: {
  selectedHours: HoursId;
  selectedPrice: PriceTier;
  onSelectHours: (id: HoursId) => void;
  onSelectPrice: (id: PriceTier) => void;
}) {
  const closesPerMonth = HOURS_OPTIONS.find((o) => o.id === selectedHours)!.closes;
  const projectedMonthly = closesPerMonth * selectedPrice;
  const animatedMonthly = useCountUp(projectedMonthly, 1200, `${selectedHours}-${selectedPrice}`);
  const fmt = (n: number) => '$' + n.toLocaleString('en-US');
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el || !('IntersectionObserver' in window)) return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          logAnalyticsEvent('calculator_view', { page: 'home' });
          obs.disconnect();
        }
      },
      { threshold: 0.25 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <section ref={sectionRef} id="calculator" className="hidden md:block py-24 px-6">
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.15 }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        className="max-w-6xl mx-auto"
      >
        <CLIFrame title="romulus / income calculator" subtitle="INTERACTIVE">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="text-center mb-10"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Calculate your income
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Pick your pace and your price. Romulus does the rest.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 mb-10">
          {/* Hours picker - horizontal timeline */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="rounded-2xl border border-white/10 bg-white/[0.02] p-6"
          >
            <p className="text-[11px] font-semibold tracking-[0.2em] uppercase text-primary-300 mb-5">
              How many hours can you commit?
            </p>
            <div className="space-y-3">
              {HOURS_OPTIONS.map((opt) => {
                const isSelected = selectedHours === opt.id;
                const Icon = opt.Icon;
                return (
                  <button
                    key={opt.id}
                    onClick={() => onSelectHours(opt.id)}
                    className={`w-full flex items-center gap-4 px-4 py-3 rounded-xl border transition-all text-left ${
                      isSelected
                        ? 'border-primary-500/60 bg-primary-500/10'
                        : 'border-white/10 bg-white/[0.02] hover:border-white/20 hover:bg-white/[0.04]'
                    }`}
                  >
                    <div
                      className={`w-10 h-10 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                        isSelected
                          ? 'border-primary-400 bg-primary-500/20'
                          : 'border-primary-500/30 bg-primary-500/[0.06]'
                      }`}
                      style={{
                        boxShadow: isSelected
                          ? '0 0 30px rgba(91,58,58,0.6)'
                          : '0 0 12px rgba(91,58,58,0.15)',
                      }}
                    >
                      <Icon className={`w-4 h-4 ${isSelected ? 'text-primary-200' : 'text-primary-300'}`} />
                    </div>
                    <div>
                      <p className={`text-sm font-semibold ${isSelected ? 'text-white' : 'text-gray-200'}`}>
                        {opt.label}
                      </p>
                      <p className="text-xs text-gray-500">{opt.sub}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          </motion.div>

          {/* Price per site picker */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="rounded-2xl border border-white/10 bg-white/[0.02] p-6"
          >
            <p className="text-[11px] font-semibold tracking-[0.2em] uppercase text-primary-300 mb-5">
              What will you charge per site?
            </p>
            <div className="grid grid-cols-2 gap-3 mb-8">
              {PRICE_OPTIONS.map((opt) => {
                const isSelected = selectedPrice === opt.id;
                return (
                  <button
                    key={opt.id}
                    onClick={() => onSelectPrice(opt.id)}
                    className={`px-4 py-4 rounded-xl border text-center transition-all ${
                      isSelected
                        ? 'border-primary-500/60 bg-primary-500/10'
                        : 'border-white/10 bg-white/[0.02] hover:border-white/20 hover:bg-white/[0.04]'
                    }`}
                  >
                    <p className={`text-lg font-bold ${isSelected ? 'text-white' : 'text-gray-200'}`}>
                      {opt.label}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">{opt.sub}</p>
                  </button>
                );
              })}
            </div>

            {/* Projected income card */}
            <div
              className="rounded-xl border border-primary-500/40 bg-gradient-to-b from-primary-500/[0.12] to-primary-500/[0.03] p-6 text-center"
              style={{ boxShadow: '0 0 50px rgba(91,58,58,0.35)' }}
            >
              <p className="text-[10px] font-semibold tracking-[0.2em] uppercase text-primary-300 mb-2">
                Projected monthly income
              </p>
              <div
                className="text-5xl font-bold text-white tabular-nums mb-2"
                style={{ textShadow: '0 0 40px rgba(91,58,58,0.8)' }}
              >
                {fmt(animatedMonthly)}
              </div>
              <p className="text-xs text-gray-400">
                {closesPerMonth} sites x {fmt(selectedPrice)} each
              </p>
            </div>
          </motion.div>
          </div>
        </CLIFrame>
      </motion.div>
    </section>
  );
}

// ============================================================
// Desktop: 12-Month Growth Projection (reused from funnel)
// ============================================================

function EarningsProjectionChart({ target }: { target: number }) {
  const curve = [0.30, 0.45, 0.60, 0.75, 0.88, 0.95, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0];
  const data = curve.map((pct, i) => ({
    month: `M${i + 1}`,
    earnings: Math.round(pct * target),
  }));
  const month1 = data[0].earnings;
  const steady = data[data.length - 1].earnings;
  const fmt = (n: number) => '$' + Math.round(n).toLocaleString('en-US');

  const formatYAxis = (value: number) => {
    if (value >= 10000) return `$${(value / 1000).toFixed(0)}k`;
    if (value >= 1000) return `$${(value / 1000).toFixed(1)}k`;
    return `$${value}`;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{ duration: 0.55, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
      className="w-full rounded-xl border border-white/[0.12] bg-transparent backdrop-blur-sm p-6"
    >
      <div className="flex items-center justify-between mb-4">
        <p className="text-[11px] font-semibold tracking-[0.2em] uppercase text-primary-300">
          12-month growth goal
        </p>
        <div className="flex items-center gap-1.5">
          <TrendingUp className="w-3.5 h-3.5 text-primary-300" />
          <span className="text-xs text-gray-500 font-mono tabular-nums">Mo 1 → 12</span>
        </div>
      </div>

      <div className="w-full h-[220px]">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="desktopProjGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#B08080" stopOpacity={0.55} />
                <stop offset="100%" stopColor="#B08080" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
            <XAxis
              dataKey="month"
              tick={{ fill: 'rgba(255,255,255,0.35)', fontSize: 11 }}
              axisLine={{ stroke: 'rgba(255,255,255,0.08)' }}
              tickLine={false}
              interval={1}
            />
            <YAxis
              tickFormatter={formatYAxis}
              tick={{ fill: 'rgba(255,255,255,0.35)', fontSize: 11 }}
              axisLine={false}
              tickLine={false}
              width={48}
            />
            <RechartsTooltip
              cursor={{ stroke: 'rgba(255,255,255,0.12)' }}
              contentStyle={{
                backgroundColor: 'rgba(17,17,17,0.95)',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: 10,
                padding: '8px 12px',
                boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
              }}
              labelStyle={{ color: 'rgba(255,255,255,0.5)', fontSize: 10, marginBottom: 2 }}
              itemStyle={{ color: '#fff', fontSize: 13, fontWeight: 600 }}
              formatter={(value) => [fmt(Number(value)), 'Goal']}
            />
            <Area
              type="monotone"
              dataKey="earnings"
              stroke="#B08080"
              strokeWidth={2.5}
              fill="url(#desktopProjGrad)"
              dot={false}
              activeDot={{ r: 5, fill: '#fff', stroke: '#B08080', strokeWidth: 2 }}
              isAnimationActive
              animationDuration={1400}
              animationEasing="ease-out"
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <div className="flex items-center justify-between mt-4 pt-4 border-t border-white/5">
        <div>
          <p className="text-[10px] uppercase tracking-wider text-gray-600 font-semibold">Month 1 goal</p>
          <p className="text-white font-bold text-sm tabular-nums">{fmt(month1)}</p>
        </div>
        <ArrowRight className="w-3.5 h-3.5 text-gray-700" />
        <div className="text-right">
          <p className="text-[10px] uppercase tracking-wider text-gray-600 font-semibold">Steady state</p>
          <p className="text-white font-bold text-sm tabular-nums">{fmt(steady)}</p>
        </div>
      </div>
    </motion.div>
  );
}


// ============================================================
// Desktop: Niche Wheel section (adapted from funnel)
// ============================================================

function NicheShowcase() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el || !('IntersectionObserver' in window)) return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          logAnalyticsEvent('niche_wheel_view', { page: 'home' });
          obs.disconnect();
        }
      },
      { threshold: 0.25 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const SIZE = 440;
  const R_OUTER = SIZE / 2;
  const R_INNER = 48;
  const LABEL_R = R_OUTER * 0.68;
  const CX = R_OUTER;
  const CY = R_OUTER;

  const wedges = NICHE_OPTIONS.map((opt, i) => {
    const startDeg = -90 + i * 60;
    const endDeg   = -90 + (i + 1) * 60;
    const startRad = (startDeg * Math.PI) / 180;
    const endRad   = (endDeg   * Math.PI) / 180;
    const midRad   = (startRad + endRad) / 2;

    const xOuterStart = CX + R_OUTER * Math.cos(startRad);
    const yOuterStart = CY + R_OUTER * Math.sin(startRad);
    const xOuterEnd   = CX + R_OUTER * Math.cos(endRad);
    const yOuterEnd   = CY + R_OUTER * Math.sin(endRad);
    const xInnerStart = CX + R_INNER * Math.cos(endRad);
    const yInnerStart = CY + R_INNER * Math.sin(endRad);
    const xInnerEnd   = CX + R_INNER * Math.cos(startRad);
    const yInnerEnd   = CY + R_INNER * Math.sin(startRad);

    const d = [
      `M ${xOuterStart},${yOuterStart}`,
      `A ${R_OUTER},${R_OUTER} 0 0,1 ${xOuterEnd},${yOuterEnd}`,
      `L ${xInnerStart},${yInnerStart}`,
      `A ${R_INNER},${R_INNER} 0 0,0 ${xInnerEnd},${yInnerEnd}`,
      'Z',
    ].join(' ');

    const labelX = CX + LABEL_R * Math.cos(midRad);
    const labelY = CY + LABEL_R * Math.sin(midRad);

    return { ...opt, d, labelX, labelY };
  });

  return (
    <section ref={sectionRef} className="hidden md:block py-24 px-6">
      <div className="max-w-6xl mx-auto">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="rounded-2xl border border-white/[0.12] backdrop-blur-sm p-8" style={{ background: 'rgba(0,0,0,0.35)' }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Who needs websites?
            </h2>
            <p className="text-gray-400 mb-6 leading-relaxed">
              Millions of local service businesses still don't have a website.
              They're leaving money on the table,{' '}
              <span className="text-white font-semibold">and so are you</span>.
              Pick any trade and Romulus finds hundreds in your area.
            </p>
            <div className="space-y-3">
              {NICHE_OPTIONS.map((opt) => {
                const Icon = opt.Icon;
                return (
                  <div key={opt.id} className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-primary-500/15 flex items-center justify-center">
                      <Icon className="w-4 h-4 text-primary-300" />
                    </div>
                    <span className="text-sm text-gray-300 font-medium">{opt.label}</span>
                  </div>
                );
              })}
            </div>
          </motion.div>

          {/* Radial niche wheel - decorative */}
          <motion.div
            initial={{ opacity: 0, scale: 0.92 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="relative mx-auto"
            style={{ width: SIZE, height: SIZE }}
          >
            <svg
              width={SIZE}
              height={SIZE}
              viewBox={`0 0 ${SIZE} ${SIZE}`}
              className="overflow-visible"
            >
              <circle
                cx={CX}
                cy={CY}
                r={R_OUTER + 4}
                fill="none"
                stroke="rgba(91,58,58,0.22)"
                strokeWidth={1}
              />
              {wedges.map((w, i) => (
                <motion.path
                  key={w.id}
                  d={w.d}
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.1 + i * 0.08 }}
                  fill="rgba(255,255,255,0.025)"
                  stroke="rgba(255,255,255,0.1)"
                  strokeWidth={1.5}
                  className="hover:fill-[rgba(176,128,128,0.2)] hover:stroke-[rgba(196,148,148,0.7)] transition-colors cursor-default"
                />
              ))}
              <circle
                cx={CX}
                cy={CY}
                r={R_INNER - 2}
                fill="#000"
                stroke="rgba(255,255,255,0.12)"
                strokeWidth={1.5}
              />
              <circle cx={CX} cy={CY} r={6} fill="rgba(176,128,128,0.95)">
                <animate attributeName="r" values="6;8;6" dur="2.5s" repeatCount="indefinite" />
              </circle>
            </svg>

            {wedges.map((w) => {
              const Icon = w.Icon;
              return (
                <div
                  key={w.id}
                  className="absolute flex flex-col items-center gap-1.5 pointer-events-none"
                  style={{
                    left: w.labelX,
                    top: w.labelY,
                    transform: 'translate(-50%, -50%)',
                  }}
                >
                  <Icon className="w-6 h-6 text-primary-300" />
                  <span className="text-[11px] font-semibold whitespace-nowrap text-gray-300">
                    {w.label}
                  </span>
                </div>
              );
            })}
          </motion.div>
        </div>
      </div>
    </section>
  );
}

// ============================================================
// Desktop: Growth + ROI section (chart + money bar side by side)
// ============================================================

function GrowthAndROI({
  projectedMonthly,
}: {
  projectedMonthly: number;
}) {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el || !('IntersectionObserver' in window)) return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          logAnalyticsEvent('growth_roi_view', { page: 'home' });
          obs.disconnect();
        }
      },
      { threshold: 0.25 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="hidden md:block py-24 px-6">
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            See your growth trajectory
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto">
            Conservative ramp-up to your target. Month 1 starts at 30%, steady state by month 7.
            These are goals, not promises.
          </p>
        </motion.div>

        <div className="max-w-2xl mx-auto">
          <EarningsProjectionChart target={projectedMonthly} />
        </div>

        <p className="text-[11px] text-gray-600 leading-relaxed text-center max-w-lg mx-auto mt-6">
          Illustrative goal, not a guarantee of earnings. Results vary. See our{' '}
          <Link to="/terms" className="text-gray-500 underline underline-offset-2 hover:text-gray-400">
            Terms
          </Link>{' '}
          for the full earnings disclaimer.
        </p>
      </div>
    </section>
  );
}

// ============================================================
// Mobile: Full landing page with desktop handoff at bottom
// ============================================================

function MobileLandingPage({
  typedTitle,
  onStart,
}: {
  typedTitle: { text: string; done: boolean };
  onStart: () => void;
}) {
  return (
    <div className="md:hidden min-h-screen flex flex-col">
      {/* Logo */}
      <div className="flex items-center justify-center pt-5 pb-2">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-md border-2 border-white bg-black flex items-center justify-center overflow-hidden">
            <img src="/wolfromulus.png" alt="Romulus" className="w-6 h-6 object-contain" />
          </div>
          <span className="text-lg font-bold text-white font-roman tracking-wider">ROMVLVS</span>
        </div>
      </div>

      <div className="flex-1 flex flex-col items-center px-5 pt-6 pb-10">
        <p className="text-primary-400 text-[11px] font-medium tracking-[0.25em] uppercase mb-3">
          Introducing
        </p>
        <h1
          className="text-5xl font-bold text-white font-roman tracking-[0.08em] mb-5 text-center min-h-[3.5rem]"
          style={{ textShadow: '0 0 80px rgba(91,58,58,0.5)' }}
        >
          {typedTitle.text}
          <span
            aria-hidden
            className="inline-block w-[3px] h-[0.85em] align-middle bg-primary-400 ml-1 animate-pulse"
            style={{ opacity: typedTitle.done ? 0 : 1, transition: 'opacity 0.4s' }}
          />
        </h1>

        <p className="text-center text-xl text-white mb-2 font-semibold leading-tight">
          You've tried side hustles before.
        </p>
        <p className="text-center text-[15px] text-gray-400 mb-6 leading-relaxed max-w-sm">
          None of them did the work <span className="text-white font-semibold">for</span> you.
        </p>

        <MobileLiveTicker />

        {/* Four-step workflow - vertical timeline */}
        <motion.div
          className="w-full max-w-sm mb-12"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.15 }}
          variants={{
            hidden: {},
            visible: { transition: { staggerChildren: 0.13, delayChildren: 0.05 } },
          }}
        >
          {(() => {
            const steps = [
              { Icon: Target, label: 'Tap 1', body: <>Romulus finds <span className="text-white font-semibold">300 local businesses</span> without websites</>, isDone: false },
              { Icon: Zap, label: 'Tap 2', body: <>Romulus builds them all a stunning site in <span className="text-white font-semibold">under a minute</span></>, isDone: false },
              { Icon: Send, label: 'Tap 3', body: <>Email + SMS pitches fire out with <span className="text-white font-semibold">live previews</span></>, isDone: false },
              { Icon: PackageCheck, label: 'Done. You sleep.', body: <>Buyer pays → <span className="text-white font-semibold">Romulus auto-delivers the files</span>. You literally do nothing else.</>, isDone: true },
            ];
            return steps.map(({ Icon, label, body, isDone }, i) => {
              const isLast = i === steps.length - 1;
              return (
                <motion.div
                  key={label}
                  variants={{
                    hidden: { opacity: 0, x: -16 },
                    visible: { opacity: 1, x: 0, transition: { duration: 0.5, ease: [0.16, 1, 0.3, 1] } },
                  }}
                  whileTap={{ scale: 0.98 }}
                  onTap={() => haptic(isDone ? [8, 30, 8] : 8)}
                  className="flex items-start gap-4 cursor-pointer"
                >
                  <div className="relative flex-shrink-0 flex flex-col items-center">
                    <div
                      className={`w-9 h-9 rounded-full flex items-center justify-center bg-black z-10 ${
                        isDone ? 'border border-primary-300' : 'border border-primary-500/50'
                      }`}
                      style={isDone ? { boxShadow: '0 0 25px rgba(91,58,58,0.65)' } : undefined}
                    >
                      <Icon className={`w-4 h-4 ${isDone ? 'text-primary-200' : 'text-primary-400'}`} />
                    </div>
                    {!isLast && (
                      <div className="w-px flex-1 bg-gradient-to-b from-primary-500/40 to-primary-500/40 mt-1 mb-1" style={{ minHeight: '28px' }} />
                    )}
                  </div>
                  <div className={`flex-1 pt-1 ${isLast ? '' : 'pb-7'}`}>
                    <p className={`text-[11px] font-semibold tracking-[0.18em] uppercase mb-1 ${isDone ? 'text-primary-300' : 'text-primary-400'}`}>{label}</p>
                    <p className="text-[14px] text-gray-200 leading-snug">{body}</p>
                  </div>
                </motion.div>
              );
            });
          })()}
        </motion.div>

        {/* Comparison table */}
        <motion.div
          className="w-full max-w-sm mb-12"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        >
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b border-white/20">
                <th className="text-left text-[9px] font-semibold tracking-[0.2em] uppercase text-gray-500 pb-2.5 pr-3 border-r border-white/15">Stage</th>
                <th className="text-center text-[9px] font-semibold tracking-[0.2em] uppercase text-primary-300 pb-2.5 px-3 border-r border-white/15">Romulus</th>
                <th className="text-center text-[9px] font-semibold tracking-[0.2em] uppercase text-gray-500 pb-2.5 pl-3 w-[78px]">Others</th>
              </tr>
            </thead>
            <tbody>
              {[
                { stage: 'Scrape leads', romulus: '10s', others: '~1 hr' },
                { stage: 'Build site', romulus: '5s', others: 'days' },
                { stage: 'Send pitch', romulus: '30s', others: 'manual' },
                { stage: 'Buyer sees demo', romulus: 'instant', others: '1–2 day call' },
                { stage: 'Collect payment', romulus: 'auto', others: 'chase' },
                { stage: 'Deliver files', romulus: 'auto', others: 'manual' },
              ].map((row, i) => (
                <motion.tr
                  key={row.stage}
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, amount: 0.1 }}
                  transition={{ duration: 0.4, delay: 0.1 + i * 0.06, ease: 'easeOut' }}
                >
                  <td className="text-[14px] text-gray-200 py-2.5 pr-3 leading-tight border-r border-white/15">{row.stage}</td>
                  <td className="text-center text-[14px] text-white font-semibold tabular-nums py-2.5 px-3 border-r border-white/15">{row.romulus}</td>
                  <td className="text-center text-[12px] text-gray-500 tabular-nums py-2.5 pl-3">{row.others}</td>
                </motion.tr>
              ))}
            </tbody>
            <tfoot>
              <motion.tr
                className="border-t-2 border-primary-500/40"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.6, delay: 0.55 }}
              >
                <td className="text-[10px] tracking-[0.25em] uppercase text-primary-300 pt-3 pb-3 pr-3 border-r border-white/15 bg-primary-500/[0.07]">Total</td>
                <td
                  className="text-center text-[14px] text-white font-bold tabular-nums pt-3 pb-3 px-3 border-r border-white/15 bg-primary-500/[0.07]"
                  style={{ textShadow: '0 0 18px rgba(91,58,58,0.7)' }}
                >
                  ~45s
                </td>
                <td className="text-center text-[12px] text-gray-400 tabular-nums pt-3 pb-3 pl-3 bg-primary-500/[0.07]">1–2 days</td>
              </motion.tr>
            </tfoot>
          </table>
        </motion.div>

        {/* Primary CTA - opens the onboarding funnel (same flow as desktop) */}
        <div className="w-full max-w-sm">
          <Button
            size="lg"
            onClick={onStart}
            className="w-full h-14 text-base group"
          >
            <span className="flex items-center justify-center gap-2">
              Calculate income
              <ArrowRight className="w-5 h-5 transform group-hover:translate-x-1 transition-transform" />
            </span>
          </Button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Main HomePage
// ============================================================

export function HomePage() {
  const [selectedHours, setSelectedHours] = useState<HoursId>('4-6');
  const [selectedPrice, setSelectedPrice] = useState<PriceTier>(500);
  const [selectedPlan, setSelectedPlan] = useState<PlanId>('starter');
  const [loading, setLoading] = useState(false);
  // TRUE ON THE FIRST PAINT, not after an effect.
  //
  // This was the split-second of landing page behind the funnel. `showOnboarding`
  // started false and an effect flipped it, and the effect itself waited on
  // useIsMobile(), which ALSO starts false and corrects in an effect - so a phone
  // rendered the marketing page, then the hook settled, then the state flipped,
  // then the funnel mounted over it. Three paints before the walkthrough existed.
  //
  // isMobileNow() is synchronous, so the funnel is in the very first committed
  // render and there is no frame where the landing page is the page. On mobile
  // the walkthrough IS the site.
  const [showOnboarding, setShowOnboarding] = useState(() => isMobileNow());
  const { user, userData, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const typedTitle = useTypewriter('ROMVLVS', 130, 350);

  // MOBILE DELTA 1 of 2 (2026-07-24): a phone never sees the marketing page.
  // The ads send people here to do the walkthrough, so the funnel opens over
  // the landing page immediately and the questions are the first thing on
  // screen. Desktop is untouched - it still lands on the marketing page and
  // opens the funnel only when a CTA is pressed.
  //
  // Gated on `!user` so a signed-in visitor hitting / isn't trapped in a
  // pre-purchase funnel instead of reaching their dashboard.
  useEffect(() => {
    if (isMobile && !user) { setShowOnboarding(true); return; }
    // A signed-in phone visitor gets it taken back down, once auth has actually
    // resolved. Firebase restores the session asynchronously, so `user` is null
    // for the first moment on every load and cannot be consulted at mount - which
    // is exactly why this direction has to be an effect while the open above is
    // synchronous. Guests, who are everyone the ads send, never see this run.
    if (isMobile && user && !authLoading) setShowOnboarding(false);
  }, [isMobile, user, authLoading]);

  // Derived projections for the calculator sections
  const closesPerMonth = HOURS_OPTIONS.find((o) => o.id === selectedHours)!.closes;
  const projectedMonthly = closesPerMonth * selectedPrice;

  useEffect(() => {
    let scroll50Fired = false;
    const onScroll = () => {
      if (scroll50Fired) return;
      const scrolled = window.scrollY + window.innerHeight;
      const total = document.documentElement.scrollHeight;
      if (total > 0 && scrolled / total >= 0.5) {
        scroll50Fired = true;
        logAnalyticsEvent('scroll_50', { page: 'home' });
        window.removeEventListener('scroll', onScroll);
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', onScroll);
    };
  }, []);

  // Guests now flow through the embedded /checkout page (Payment Element)
  // instead of redirecting to hosted Stripe Checkout. Logged-in users still
  // use hosted Checkout via createCheckout — that's the upgrade path.
  const [checkoutTransition, setCheckoutTransition] = useState<{
    x: number;
    y: number;
    planId: PlanId;
    // Set on mobile only. When present the snap ends in a redirect to Stripe's
    // hosted page instead of a route change to /checkout. Carried as a promise
    // because the session is usually already minted by the time the user taps -
    // the snap and the network call overlap rather than queue.
    hosted?: Promise<string>;
  } | null>(null);

  const handleStartCheckout = async (planId?: PlanId, originEvent?: React.MouseEvent) => {
    const effectivePlan = planId || selectedPlan;
    logAnalyticsEvent('cta_start_today_clicked', {
      location: 'home_page',
      plan_id: effectivePlan,
      user_logged_in: !!user,
    });

    if (user && userData?.subscriptionActive) {
      navigate('/dashboard');
      return;
    }

    // SIGNED IN, no active subscription.
    //
    // This branch is why desktop was still landing on Stripe's hosted page after
    // the guest paths were fixed: it predates the device split entirely and went
    // straight to hosted for everyone. Desktop now gets the same embedded
    // Payment Element a guest gets; CheckoutPage links the finished subscription
    // to the signed-in uid instead of sending them to /register.
    if (user && !isPhoneDevice()) {
      logAnalyticsEvent('begin_checkout', {
        plan_id: effectivePlan,
        flow: 'authenticated_embedded',
        currency: 'USD',
        value: PLAN_CONFIGS[effectivePlan].price,
        items: [{ item_id: effectivePlan, item_name: PLAN_CONFIGS[effectivePlan].name, price: PLAN_CONFIGS[effectivePlan].price }],
      });
      prefetchCheckoutSubscription(effectivePlan).catch(() => {});
      const ux = originEvent?.clientX ?? window.innerWidth / 2;
      const uy = originEvent?.clientY ?? window.innerHeight / 2;
      setCheckoutTransition({ x: ux, y: uy, planId: effectivePlan });
      return;
    }

    if (user) {
      // Signed in ON A PHONE — hosted, same as every other mobile path.
      setLoading(true);
      try {
        const createCheckout = httpsCallable<
          { planId: PlanId; metaFbp?: string; metaFbc?: string },
          { sessionUrl: string }
        >(functions, 'createCheckout');
        logAnalyticsEvent('begin_checkout', {
          plan_id: effectivePlan,
          flow: 'authenticated',
          currency: 'USD',
          value: PLAN_CONFIGS[effectivePlan].price,
          items: [{ item_id: effectivePlan, item_name: PLAN_CONFIGS[effectivePlan].name, price: PLAN_CONFIGS[effectivePlan].price }],
        });
        const result = await createCheckout({ planId: effectivePlan, ...getMetaBrowserIds() });
        // Stripe's hosted page carries no pixel of ours - fire on the way out.
        metaInitiateCheckout(effectivePlan, PLAN_CONFIGS[effectivePlan].name, Math.round(PLAN_CONFIGS[effectivePlan].price * 100));
        window.location.href = result.data.sessionUrl;
      } catch (error: unknown) {
        const message = error instanceof Error ? error.message : 'Failed to start checkout';
        toast.error(message);
        setLoading(false);
      }
      return;
    }

    // GUEST PATH — split by device on purpose (2026-07-24).
    //
    // MOBILE goes straight to Stripe's hosted Checkout. Nothing is rendered in
    // between: no thanos snap, no navigation to /checkout, no Payment Element
    // skeleton. The previous arrangement played the 1.3s snap, mounted the
    // embedded checkout page, drew its skeleton, and THEN bounced to hosted -
    // roughly two seconds of animation for a screen the user never gets to use.
    // On a phone that is the single worst place to spend attention.
    //
    // Deliberately NOT prefetched. prefetchCheckoutSubscription calls
    // createGuestSubscription, which does stripe.subscriptions.create() - a real
    // Customer plus an incomplete Subscription, on every tap, discarded the
    // moment we redirect. That is what left 100+ incomplete_expired
    // subscriptions in the live account. A hosted Checkout Session creates no
    // Customer until the payment actually completes.
    // isPhoneDevice(), not the hook and not isMobileNow(). This is a routing
    // GUARD, so it cannot use useIsMobile() (starts false, corrects in an effect,
    // so one wrong read sends a phone into the desktop embedded checkout). It
    // also must not use isMobileNow(), whose `innerWidth < 768` term sends a
    // DESKTOP browser in a narrow window out to hosted Checkout — desktop is
    // embedded, always. UA only, and every real phone carries one.
    if (isPhoneDevice()) {
      setLoading(true);
      logAnalyticsEvent('begin_checkout', {
        plan_id: effectivePlan,
        flow: 'guest_hosted_mobile',
        currency: 'USD',
        value: PLAN_CONFIGS[effectivePlan].price,
        items: [{ item_id: effectivePlan, item_name: PLAN_CONFIGS[effectivePlan].name, price: PLAN_CONFIGS[effectivePlan].price }],
      });
      // The snap, on mobile too. It starts on the tap frame and runs WHILE the
      // session resolves - usually already resolved, since the paywall prefetches
      // it as soon as the plan cards render - so the animation is cover, not
      // added latency. It also ends on a full black frame, which is what the
      // browser keeps showing while checkout.stripe.com loads.
      const hosted = getHostedCheckoutUrl(effectivePlan);
      hosted.catch(() => {});
      const mx = originEvent?.clientX ?? window.innerWidth / 2;
      const my = originEvent?.clientY ?? window.innerHeight / 2;
      setCheckoutTransition({ x: mx, y: my, planId: effectivePlan, hosted });
      return;
    }

    // DESKTOP: particle-burst transition into the embedded checkout page. The
    // snap and the skeleton both earn their keep here - the Payment Element
    // really does render at the end of them.
    logAnalyticsEvent('begin_checkout', {
      plan_id: effectivePlan,
      flow: 'guest_embedded',
      currency: 'USD',
      value: PLAN_CONFIGS[effectivePlan].price,
      items: [{ item_id: effectivePlan, item_name: PLAN_CONFIGS[effectivePlan].name, price: PLAN_CONFIGS[effectivePlan].price }],
    });

    // Kick off the cloud function call NOW, in parallel with the 1.3s thanos
    // snap, so by the time CheckoutPage mounts the clientSecret is already in
    // sessionStorage. Fire-and-forget — failures fall back to the in-mount
    // call on /checkout. .catch swallows so we don't fire an unhandled
    // rejection if the user backs out before navigating.
    prefetchCheckoutSubscription(effectivePlan).catch(() => {});

    const x = originEvent?.clientX ?? window.innerWidth / 2;
    const y = originEvent?.clientY ?? window.innerHeight / 2;
    setCheckoutTransition({ x, y, planId: effectivePlan });
  };


  const scrollToSection = (id: string) => {
    logAnalyticsEvent('nav_section_clicked', { section: id });
    const element = document.getElementById(id);
    if (element) {
      const offset = id === 'demo' ? 100 : 80;
      const elementPosition = element.getBoundingClientRect().top + window.scrollY;
      window.scrollTo({
        top: elementPosition - offset,
        behavior: 'smooth'
      });
    }
  };


  return (
    <HeroBackground>
      <div className="min-h-screen">
        {/* Pre-checkout funnel - opens over the landing page when the user
            clicks any top-level "Get started" CTA. Full-screen overlay on
            both mobile and desktop. Its onCheckout handler always locks
            the plan to Starter ($49) for lowest-friction conversion. */}
        <AnimatePresence>
          {showOnboarding && (
            <OnboardingFunnel
              loading={loading}
              onClose={(atStep) => {
                logAnalyticsEvent('onboarding_dismissed', { at_step: atStep });
                setShowOnboarding(false);
              }}
              onCheckout={(planId) => {
                setSelectedPlan(planId);
                handleStartCheckout(planId);
              }}
            />
          )}
        </AnimatePresence>

        <AnimatePresence>
          {checkoutTransition && (
            <CheckoutTransition
              originX={checkoutTransition.x}
              originY={checkoutTransition.y}
              onComplete={async () => {
                if (!checkoutTransition.hosted) {
                  navigate(`/checkout?plan=${checkoutTransition.planId}`);
                  return;
                }
                try {
                  const sessionUrl = await checkoutTransition.hosted;
                  logAnalyticsEvent('checkout_hosted_redirect', {
                    plan_id: checkoutTransition.planId,
                    value: PLAN_CONFIGS[checkoutTransition.planId].price,
                    surface: 'mobile',
                  });
                  const hostedPlan = PLAN_CONFIGS[checkoutTransition.planId];
                  metaInitiateCheckout(checkoutTransition.planId, hostedPlan.name, Math.round(hostedPlan.price * 100));
                  window.location.href = sessionUrl;
                } catch (error: unknown) {
                  // Snap already played, so put the page back rather than
                  // leaving them on a black screen.
                  setCheckoutTransition(null);
                  setLoading(false);
                  toast.error(error instanceof Error ? error.message : 'Failed to start checkout');
                }
              }}
            />
          )}
        </AnimatePresence>

        {/* ───────────────────────── MOBILE: LANDING + ONBOARDING CTA ────────────── */}
        {/* Not rendered at all while the funnel is up. The funnel is a full-screen
            opaque overlay, so this was never visible on purpose - but it was still
            mounted, still booting its typewriter and its three.js background, and
            still the thing on screen for any frame the overlay had not painted. */}
        {!showOnboarding && (
        <MobileLandingPage
          typedTitle={typedTitle}
          onStart={() => {
            logAnalyticsEvent('onboarding_started', { source: 'home_mobile_cta' });
            setShowOnboarding(true);
          }}
        />
        )}

        {/* ───────────────────────── DESKTOP ───────────────────────── */}
        <nav
          className="hidden md:block fixed top-0 left-0 right-0 z-50"
          style={{
            background: 'rgba(0,0,0,0.8)',
            backdropFilter: 'blur(20px)',
            borderBottom: '1px solid rgba(255,255,255,0.05)',
          }}
        >
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg border-2 border-white bg-black flex items-center justify-center overflow-hidden">
                <img src="/wolfromulus.png" alt="Romulus" className="w-8 h-8 object-contain" />
              </div>
              <span className="text-2xl font-bold text-white font-roman tracking-wider">ROMVLVS</span>
            </Link>

            <div className="hidden md:flex items-center gap-8">
              <button
                onClick={() => scrollToSection('demo')}
                className="text-gray-400 hover:text-white transition-colors text-sm"
              >
                Demo
              </button>
              <button
                onClick={() => scrollToSection('calculator')}
                className="text-gray-400 hover:text-white transition-colors text-sm"
              >
                Calculator
              </button>
            </div>

            <Button
              onClick={() => {
                logAnalyticsEvent('onboarding_started', { source: 'home_nav' });
                setShowOnboarding(true);
              }}
              className="group"
            >
              <span className="flex items-center gap-2">
                Get started
                <ArrowRight className="w-4 h-4 transform group-hover:translate-x-1 transition-transform" />
              </span>
            </Button>
          </div>
        </nav>

        {/* Hero */}
        <section className="hidden md:flex min-h-screen flex-col items-center justify-center px-4 pt-20">
          <div className="w-full max-w-7xl mx-auto">
            <div className="text-center mb-8">
              <p className="text-primary-400 text-sm font-medium tracking-widest uppercase mb-4 mt-16">
                Introducing
              </p>
              <h1
                className="text-5xl md:text-7xl font-bold text-white font-roman tracking-[0.2em] mb-4 min-h-[5rem] md:min-h-[6.5rem]"
                style={{
                  textShadow: '0 0 80px rgba(91,58,58,0.4)',
                }}
              >
                {typedTitle.text}
                <span
                  aria-hidden
                  className="inline-block w-[4px] md:w-[5px] h-[0.8em] align-middle bg-primary-400 ml-2 animate-pulse"
                  style={{ opacity: typedTitle.done ? 0 : 1, transition: 'opacity 0.4s' }}
                />
              </h1>
              <p className="text-lg text-gray-400 max-w-2xl mx-auto mb-8 leading-relaxed">
                Find local businesses without websites, generate stunning sites in under a minute, and close the sale.{' '}
                <span className="text-white font-semibold">Keep 100% of Every Dollar Made.</span>
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-10">
                <Button
                  size="lg"
                  onClick={() => {
                    logAnalyticsEvent('onboarding_started', { source: 'home_hero' });
                    setShowOnboarding(true);
                  }}
                  className="w-full sm:w-auto h-12 px-6 text-base group"
                >
                  <span className="flex items-center gap-2">
                    Get started
                    <ArrowRight className="w-5 h-5 transform group-hover:translate-x-1 transition-transform" />
                  </span>
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  onClick={() => scrollToSection('demo')}
                  className="w-full sm:w-auto h-12 px-6 text-base"
                >
                  Watch demo
                </Button>
              </div>
            </div>

            <div id="demo">
              <LiveDemoWindow />
              <p className="text-center text-gray-600 text-xs mt-3">*Fictional demo for illustration purposes</p>
            </div>
          </div>
        </section>

        {/* Features section removed - will be replaced with YouTube video */}

        {/* Niche Wheel */}
        <NicheShowcase />

        {/* Income Calculator */}
        <IncomeCalculator
          selectedHours={selectedHours}
          selectedPrice={selectedPrice}
          onSelectHours={(id) => { setSelectedHours(id); logAnalyticsEvent('calculator_hours_selected', { hours: id }); }}
          onSelectPrice={(id) => { setSelectedPrice(id); logAnalyticsEvent('calculator_price_selected', { price: id }); }}
        />

        {/* Growth Projection */}
        <GrowthAndROI projectedMonthly={projectedMonthly} />


        {/* Footer */}
        <footer className="hidden md:block py-8 px-6 border-t border-white/10">
          <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <img src="/wolfromulus.png" alt="Romulus" className="w-12 h-12" />
              <span className="text-sm text-gray-500">Romulus</span>
            </div>
            <div className="flex items-center gap-6 text-sm text-gray-500">
              <Link to="/login" className="hover:text-white transition-colors">Login</Link>
              <Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
              <Link to="/terms" className="hover:text-white transition-colors">Terms</Link>
            </div>
          </div>
        </footer>
      </div>

      <style>{`
        @keyframes fadeInLine {
          from { opacity: 0; transform: translateY(3px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes slideIn {
          from { opacity: 0; transform: translateX(-10px); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes scaleIn {
          from { opacity: 0; transform: scale(0.9); }
          to { opacity: 1; transform: scale(1); }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes bounceIn {
          0% { opacity: 0; transform: scale(0.3); }
          50% { transform: scale(1.05); }
          70% { transform: scale(0.9); }
          100% { opacity: 1; transform: scale(1); }
        }
        @keyframes pulse {
          0%, 100% { box-shadow: 0 0 20px rgba(91,58,58,0.6); }
          50% { box-shadow: 0 0 30px rgba(91,58,58,0.9); }
        }
      `}</style>
    </HeroBackground>
  );
}
